
import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import type { RaceEvent, ExternalEvent, FavoriteGroupRide } from '../types';
import RaceEditorModal from '../components/AddRaceModal';
import CategorySelectionModal from '../components/CategorySelectionModal';
import RaceCard from '../components/RaceCard';
import ExternalEventCard from '../components/ExternalEventCard';
import { PlusIcon } from '../components/icons/PlaybackIcons';
import { raceEventService } from '../services/raceService';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import SEO from '../components/SEO';

const RacesListPage: React.FC = () => {
  const { currentUser, isAdmin } = useAuth();
  const [allRaces, setAllRaces] = useState<RaceEvent[]>([]);
  const [plannedEvents, setPlannedEvents] = useState<ExternalEvent[]>([]);
  const [favoriteGroupRides, setFavoriteGroupRides] = useState<FavoriteGroupRide[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // -- Modals State --
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingRace, setEditingRace] = useState<RaceEvent | undefined>(undefined);
  
  const [isSelectionOpen, setIsSelectionOpen] = useState(false);
  const [selectedRace, setSelectedRace] = useState<RaceEvent | null>(null);

  // State for card actions
  const [processingEventId, setProcessingEventId] = useState<string | null>(null);

  // We assume mock races are immutable "standard" races, so anything NOT in the standard list is custom.
  const [customRaceIds, setCustomRaceIds] = useState<Set<string>>(new Set());
  const [participatedRaceIds, setParticipatedRaceIds] = useState<Set<string>>(new Set());

  const fetchData = async () => {
      setIsLoading(true);
      try {
          // 1. Fetch All Race Events (to find participation)
          const races = await raceEventService.getAllRaceEvents();
          setAllRaces(races);
          
          const standardIds = new Set(['emerald-city-classic', 'cascade-climbfest', 'puget-sound-gp', 'rainier-road-race', 'giro-de-san-francisco']);
          const custom = new Set(races.filter(r => !standardIds.has(r.id)).map(r => r.id));
          setCustomRaceIds(custom);

          // 2. Fetch User Profile (for Planned Events & Favorite Group Rides)
          if (currentUser) {
              const profile = await userService.getUserProfile(currentUser.uid);
              if (profile && profile.plannedEvents) {
                  setPlannedEvents(profile.plannedEvents);
              }
              if (profile && profile.favoriteGroupRides) {
                  setFavoriteGroupRides(profile.favoriteGroupRides);
              }

                  // 3. Determine participation for past races (since lightweight listing has empty categories)
                  const todayDate = new Date();
                  todayDate.setHours(0, 0, 0, 0);
                  
                  // Group ride occurrences inherit the series owner, so owners still need a participation check
                  const pastRaces = races.filter(r => new Date(r.date) < todayDate && (r.ownerId !== currentUser.uid || !!r.seriesId));
                  
                  const myRaceIds = new Set<string>();

                  await Promise.all(pastRaces.map(async (race) => {
                      try {
                          const catsSnapshot = await getDocs(collection(db, 'races', race.id, 'categories'));
                          let userInRace = false;
                          catsSnapshot.forEach(doc => {
                              const data = doc.data();
                              const athletes = data.athletes || [];
                              if (athletes.some((a: any) => a.userId === currentUser.uid || a.id === currentUser.uid)) {
                                  userInRace = true;
                              }
                          });
                          if (userInRace) {
                              myRaceIds.add(race.id);
                          }
                      } catch (err) {
                          console.error(`Failed to check participation for race ${race.id}`, err);
                      }
                  }));

                  setParticipatedRaceIds(myRaceIds);
          }
      } catch (e) {
          console.error("Failed to load data", e);
      } finally {
          setIsLoading(false);
      }
  };

  useEffect(() => {
      fetchData();
  }, [currentUser]);

  // --- Derived Data ---

  // SECTION 1: Planned Races (Future Calendar Events)
  const upcomingPlannedEvents = useMemo(() => {
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      
      return plannedEvents
          .filter(e => new Date(e.date) >= todayMidnight)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [plannedEvents]);

  // SECTION 2: Past Races (Participated OR Owned, < Today)
  const myPastRaces = useMemo(() => {
      if (!currentUser) return [];

      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);

      return allRaces
          .filter(race => {
              // Occurrences only count if the user actually rode (owning the series isn't attendance)
              const isOwner = race.ownerId === currentUser.uid && !race.seriesId;
              const isMyRace = isOwner || participatedRaceIds.has(race.id) ||
                             (race.categories || []).some(cat => 
                                 (cat.athletes || []).some(a => a.userId === currentUser.uid || a.id === currentUser.uid)
                             );
              const isPast = new Date(race.date) < todayMidnight;
              return isMyRace && isPast;
          })
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [allRaces, currentUser, participatedRaceIds]);


  // --- Handlers ---

  const handleOpenCreateModal = () => {
      setEditingRace(undefined);
      setIsEditorOpen(true);
  };
  
  const handleOpenEditModal = async (raceToEdit: RaceEvent) => {
      setIsLoading(true);
      try {
          const fullRace = await raceEventService.getRaceEventBySlug(raceToEdit.id);
          if (fullRace) {
              setEditingRace(fullRace);
              setIsEditorOpen(true);
          } else {
              alert("Could not load full event details.");
          }
      } catch (error) {
          console.error("Error fetching full race details:", error);
          alert("An error occurred while loading event details.");
      } finally {
          setIsLoading(false);
      }
  };

  const handleSelectGroup = (race: RaceEvent) => {
      setSelectedRace(race);
      setIsSelectionOpen(true);
  }
  
  const handleCloseEditor = () => {
      setIsEditorOpen(false);
      setEditingRace(undefined);
  }

  const handleRemovePlannedEvent = async (event: ExternalEvent) => {
      if (!currentUser) return;
      if (!confirm(`Remove ${event.name} from your calendar?`)) return;

      setProcessingEventId(event.id);
      try {
          await userService.removePlannedEvent(currentUser.uid, event);
          setPlannedEvents(prev => prev.filter(e => e.id !== event.id));
      } catch (e) {
          alert("Failed to remove event.");
      } finally {
          setProcessingEventId(null);
      }
  };

  const handleRemoveFavoriteGroupRide = async (rideId: string) => {
      if (!currentUser) return;
      if (!confirm('Remove this group ride from your saved rides?')) return;
      try {
          await userService.removeFavoriteGroupRide(currentUser.uid, rideId);
          setFavoriteGroupRides(prev => prev.filter(r => r.id !== rideId));
      } catch (e) {
          alert('Failed to remove saved ride.');
      }
  };

  const handleCreateFromEvent = (_event: ExternalEvent) => {
      // Not implemented in this view directly, but could link to creation modal
      // For now, simpler to just let them view it.
  }

  const handleSaveRace = async (savedRace: RaceEvent) => {
    try {
        await raceEventService.saveRaceEvent(savedRace);

        await fetchData();
        handleCloseEditor();
    } catch (error) {
        console.error("Failed to save races", error);
        alert("Could not save the race. Please try again.");
    }
  };
  
  const handleDeleteEvent = async (raceToDelete: RaceEvent) => {
    try {
        await raceEventService.deleteRaceEvent(raceToDelete.id);
        await fetchData();
        handleCloseEditor();
    } catch (error) {
        console.error("Failed to delete event", error);
        alert("Could not delete the event. Please try again.");
    }
  };

  return (
    <div className="h-full bg-gray-900 text-white p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <SEO title="Your Events | RaceLineIQ" />
      <div className="max-w-7xl mx-auto space-y-12">
        <div className="flex justify-between items-center mb-6 border-b border-gray-700 pb-3">
            <h1 className="text-3xl font-bold text-white">Your Events</h1>
            {isAdmin && (
                <button
                    onClick={handleOpenCreateModal}
                    className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-gray-900 font-bold rounded-lg hover:bg-cyan-400 transition-colors shadow-lg shadow-cyan-500/20"
                >
                    <PlusIcon className="w-5 h-5" />
                    Add Event
                </button>
            )}
        </div>
        
        {isLoading ? (
             <div className="flex justify-center items-center h-64">
                <svg className="animate-spin h-10 w-10 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
        ) : (
            <>
                {/* SECTION 1: PLANNED RACES */}
                <section>
                    <h2 className="text-xl font-bold text-cyan-400 mb-4 border-b border-gray-800 pb-2">
                        Planned Races <span className="text-gray-500 text-sm ml-2 font-normal">Upcoming Calendar Events</span>
                    </h2>
                    
                    {upcomingPlannedEvents.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {upcomingPlannedEvents.map(event => (
                                <ExternalEventCard 
                                    key={event.id}
                                    event={event}
                                    onPlan={() => handleRemovePlannedEvent(event)} // Re-using card prop for removal interaction logic could be improved, but card handles disabled state
                                    onCreate={handleCreateFromEvent}
                                    isPlanned={true} // It is planned
                                    isProcessing={processingEventId === event.id}
                                    // Hack: We change the button text based on 'isPlanned' prop in the component
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="bg-gray-800/50 rounded-lg p-8 text-center border border-gray-700 border-dashed">
                            <p className="text-gray-400">You have no upcoming races planned.</p>
                            <p className="text-sm text-gray-500 mt-2">Use the "Find Races" page to find and add events to your calendar.</p>
                        </div>
                    )}
                </section>

                {/* SECTION 2: SAVED GROUP RIDES */}
                <section>
                    <h2 className="text-xl font-bold text-cyan-400 mb-4 border-b border-gray-800 pb-2">
                        Saved Group Rides <span className="text-gray-500 text-sm ml-2 font-normal">Your Favorites</span>
                    </h2>

                    {favoriteGroupRides.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {favoriteGroupRides
                                .slice()
                                .sort((a, b) => b.savedAt - a.savedAt)
                                .map(ride => (
                                    <div
                                        key={ride.id}
                                        className="bg-gray-800 border border-gray-700 rounded-xl p-5 shadow-lg flex flex-col justify-between gap-4 hover:border-cyan-600/50 transition-colors"
                                    >
                                        <div>
                                            <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">⭐ Saved Group Ride</span>
                                            <h3 className="font-bold text-white text-base mt-1">{ride.name}</h3>
                                            {ride.locationName && (
                                                <p className="text-gray-400 text-sm mt-1">{ride.locationName}</p>
                                            )}
                                            <p className="text-gray-500 text-xs mt-2">
                                                Saved {new Date(ride.savedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Link
                                                to={`/ride/${ride.id}`}
                                                state={{ from: 'my' }}
                                                className="flex-1 text-center px-3 py-2 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-300 text-xs font-bold rounded-lg border border-cyan-500/40 transition-colors"
                                            >
                                                View Ride
                                            </Link>
                                            <button
                                                onClick={() => handleRemoveFavoriteGroupRide(ride.id)}
                                                className="px-3 py-2 bg-gray-700 hover:bg-red-900/30 text-gray-400 hover:text-red-400 text-xs font-bold rounded-lg border border-gray-600 hover:border-red-900/50 transition-colors"
                                                title="Remove from saved rides"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    ) : (
                        <div className="bg-gray-800/50 rounded-lg p-8 text-center border border-gray-700 border-dashed">
                            <p className="text-gray-400">You haven't saved any group rides yet.</p>
                            <p className="text-sm text-gray-500 mt-2">Click "Favorite Ride" on any group ride page to save it here.</p>
                        </div>
                    )}
                </section>

                {/* SECTION 3: PAST RACES */}
                <section>
                    <h2 className="text-xl font-bold text-cyan-400 mb-4 border-b border-gray-800 pb-2">
                        Past Races <span className="text-gray-500 text-sm ml-2 font-normal">Your History</span>
                    </h2>

                    {myPastRaces.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {myPastRaces.map(race => {
                                const isCustom = customRaceIds.has(race.id);
                                return (
                                    <RaceCard 
                                        key={race.id} 
                                        race={race}
                                        isCustom={isCustom}
                                        currentUserId={currentUser?.uid}
                                        onEdit={handleOpenEditModal}
                                        onSelectGroup={handleSelectGroup}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-gray-800/50 rounded-lg p-8 text-center border border-gray-700 border-dashed">
                            <p className="text-gray-400">No past race data found.</p>
                            <p className="text-sm text-gray-500 mt-2">Races you have participated in or created will appear here after they occur.</p>
                        </div>
                    )}
                </section>
            </>
        )}
      </div>

      {isEditorOpen && (
        <RaceEditorModal 
            onSave={handleSaveRace} 
            onClose={handleCloseEditor}
            onDelete={handleDeleteEvent}
            eventToEdit={editingRace}
        />
      )}

      {isSelectionOpen && selectedRace && (
          <CategorySelectionModal 
            isOpen={isSelectionOpen}
            onClose={() => setIsSelectionOpen(false)}
            race={selectedRace}
          />
      )}
    </div>
  );
};

export default RacesListPage;
